import { useCallback, useSyncExternalStore } from 'react'
import { sentarEnOrden, type Silla } from '@domino/core'
import { z } from 'zod'
import type { ManoAnotada } from '../componentes/Libreta.js'

/**
 * La libreta personal: las partidas que el jugador anota fuera de los torneos.
 *
 * Son las del café, las de la casa de un amigo, las del club un martes. No
 * pasan por el organizador ni por el servidor: viven en el navegador del
 * teléfono, guardadas en `localStorage`. Es a propósito. No hay nadie que las
 * valide, así que no pueden mezclarse con los números de torneo, y el que anota
 * tampoco necesita red para hacerlo (el café no siempre tiene).
 *
 * La contra es la de cualquier dato local: si se borran los datos del sitio o
 * se cambia de teléfono, la libreta se pierde. La pantalla lo dice.
 *
 * Cada cuenta tiene su libreta: la clave lleva el id del jugador, y dos que
 * comparten teléfono no se ven las partidas.
 *
 * El dueño de la libreta juega siempre en el bando 1. Los otros tres se anotan
 * por nombre, y es por nombre que se arma el ranking: «Pedro» del lunes y
 * «pedro» del jueves son la misma persona.
 */

const manoSchema = z.object({
  bandoGanador: z.union([z.literal(1), z.literal(2)]),
  puntos: z.number().int().positive(),
})

const partidaSchema = z.object({
  id: z.string().min(1),
  /** Cuándo se empezó: un instante, en ISO. */
  creada: z.string(),
  /** Cuándo uno llegó al objetivo. Es la fecha con la que cuenta en el ranking. */
  terminada: z.string().nullable(),
  objetivo: z.number().int().positive(),
  companero: z.string(),
  rivales: z.tuple([z.string(), z.string()]),
  manos: z.array(manoSchema),
  /**
   * La silla que dio la salida en la primera mano, o nulo si no se anotó. Las
   * partidas guardadas antes de que existiera llegan sin ella.
   */
  salida: z.number().int().min(1).max(4).nullable().default(null),
})

export type PartidaPersonal = z.infer<typeof partidaSchema>
export type ManoPersonal = z.infer<typeof manoSchema>

/** Los objetivos que se ofrecen al empezar. */
export const OBJETIVOS = [100, 150, 200, 250] as const
export const OBJETIVO_POR_DEFECTO = 200

/* ── La cuenta de una partida ─────────────────────────────────────────── */

export interface CuentaPersonal {
  bando1: number
  bando2: number
  ganador: 1 | 2 | null
  objetivoPuntos: number
  manos: ManoAnotada[]
}

/**
 * Suma las manos. Gana el primero que llega al objetivo: como cada mano la
 * cobra un solo bando, nunca llegan los dos a la vez.
 */
export function contar(partida: Pick<PartidaPersonal, 'objetivo' | 'manos'>): CuentaPersonal {
  let bando1 = 0
  let bando2 = 0
  let ganador: 1 | 2 | null = null
  const manos = partida.manos.map((m, i) => {
    if (m.bandoGanador === 1) bando1 += m.puntos
    else bando2 += m.puntos
    if (ganador === null && (bando1 >= partida.objetivo || bando2 >= partida.objetivo)) {
      ganador = bando1 >= partida.objetivo ? 1 : 2
    }
    return { numero: i + 1, bandoGanador: m.bandoGanador, puntos: m.puntos, bando1, bando2 }
  })
  return { bando1, bando2, ganador, objetivoPuntos: partida.objetivo, manos }
}

/**
 * La partida con sus manos nuevas y la fecha de cierre al día.
 *
 * La fecha de cierre se conserva si ya la tenía: corregir el lunes una mano de
 * la partida del sábado no la muda al lunes en el ranking. Si la corrección
 * deja a nadie arriba del objetivo, la partida vuelve a estar en juego.
 */
export function conManos(
  partida: PartidaPersonal,
  manos: ManoPersonal[],
  ahora: Date = new Date(),
): PartidaPersonal {
  const { ganador } = contar({ objetivo: partida.objetivo, manos })
  const terminada = ganador === null ? null : (partida.terminada ?? ahora.toISOString())
  return { ...partida, manos, terminada }
}

/**
 * Las cuatro sillas de una partida suelta, en el orden en que se juega: el
 * dueño, el primer rival (el que se sienta a su derecha), el compañero y el
 * segundo rival. El dueño juega siempre en el bando 1.
 */
export function sillasDe(
  partida: Pick<PartidaPersonal, 'companero' | 'rivales'>,
  yo: string,
): Silla[] {
  return sentarEnOrden([
    { bando: 1, nombre: yo },
    { bando: 1, nombre: partida.companero },
    { bando: 2, nombre: partida.rivales[0] },
    { bando: 2, nombre: partida.rivales[1] },
  ])
}

/* ── Los períodos del ranking ─────────────────────────────────────────── */

export type Periodo = 'hoy' | 'semana' | 'mes' | 'ano' | 'siempre'

export const PERIODOS: readonly { id: Periodo; texto: string }[] = [
  { id: 'hoy', texto: 'Hoy' },
  { id: 'semana', texto: 'Semana' },
  { id: 'mes', texto: 'Mes' },
  { id: 'ano', texto: 'Año' },
  { id: 'siempre', texto: 'Siempre' },
]

/**
 * Desde cuándo cuenta un período, en la hora del teléfono: «hoy» es desde la
 * medianoche de acá, no la de Greenwich. La semana arranca el lunes.
 */
export function inicioDe(periodo: Periodo, ahora: Date): Date | null {
  const d = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate())
  switch (periodo) {
    case 'hoy':
      return d
    case 'semana':
      d.setDate(d.getDate() - ((d.getDay() + 6) % 7))
      return d
    case 'mes':
      return new Date(ahora.getFullYear(), ahora.getMonth(), 1)
    case 'ano':
      return new Date(ahora.getFullYear(), 0, 1)
    case 'siempre':
      return null
  }
}

/** Las partidas terminadas dentro del período, la más reciente primero. */
export function terminadasEn(
  partidas: readonly PartidaPersonal[],
  periodo: Periodo,
  ahora: Date,
): PartidaPersonal[] {
  const desde = inicioDe(periodo, ahora)?.getTime() ?? -Infinity
  return partidas
    .filter((p) => p.terminada !== null && new Date(p.terminada).getTime() >= desde)
    .sort((a, b) => (b.terminada ?? '').localeCompare(a.terminada ?? ''))
}

/* ── El ranking ───────────────────────────────────────────────────────── */

/** La clave del dueño: no es un nombre, así cambiar el apodo no lo parte en dos. */
export const YO = '__yo__'

/** «  Pedro  José » y «pedro josé» son la misma persona. */
export function claveDe(nombre: string): string {
  return nombre
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('es')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
}

export interface FilaRanking {
  clave: string
  nombre: string
  esYo: boolean
  jugadas: number
  ganadas: number
  perdidas: number
  aFavor: number
  enContra: number
  /** Ganadas sobre jugadas, de 0 a 100 y redondeado. */
  porcentaje: number
}

/**
 * Todos los que aparecen en las partidas, con lo que hizo cada uno.
 *
 * Se ordena por ganadas, después por porcentaje (el que ganó 3 de 3 va arriba
 * del que ganó 3 de 9) y por último por la diferencia de puntos.
 */
export function ranking(partidas: readonly PartidaPersonal[], miNombre: string): FilaRanking[] {
  const filas = new Map<string, FilaRanking>()

  function sumar(clave: string, nombre: string, gano: boolean, aFavor: number, enContra: number) {
    const fila =
      filas.get(clave) ??
      ({
        clave,
        nombre,
        esYo: clave === YO,
        jugadas: 0,
        ganadas: 0,
        perdidas: 0,
        aFavor: 0,
        enContra: 0,
        porcentaje: 0,
      } satisfies FilaRanking)
    fila.jugadas += 1
    if (gano) fila.ganadas += 1
    else fila.perdidas += 1
    fila.aFavor += aFavor
    fila.enContra += enContra
    filas.set(clave, fila)
  }

  for (const p of partidas) {
    const cuenta = contar(p)
    if (cuenta.ganador === null) continue
    const ganamos = cuenta.ganador === 1
    sumar(YO, miNombre, ganamos, cuenta.bando1, cuenta.bando2)
    if (p.companero.trim()) {
      sumar(claveDe(p.companero), p.companero.trim(), ganamos, cuenta.bando1, cuenta.bando2)
    }
    for (const r of p.rivales) {
      if (r.trim()) sumar(claveDe(r), r.trim(), !ganamos, cuenta.bando2, cuenta.bando1)
    }
  }

  return [...filas.values()]
    .map((f) => ({ ...f, porcentaje: Math.round((f.ganadas / f.jugadas) * 100) }))
    .sort(
      (a, b) =>
        b.ganadas - a.ganadas ||
        b.porcentaje - a.porcentaje ||
        b.aFavor - b.enContra - (a.aFavor - a.enContra) ||
        a.nombre.localeCompare(b.nombre, 'es'),
    )
}

/**
 * Los nombres ya anotados, del más usado al menos, para sugerirlos al empezar
 * otra partida: así «Pedro» se escribe igual todas las veces.
 */
export function nombresUsados(partidas: readonly PartidaPersonal[]): string[] {
  const veces = new Map<string, { nombre: string; n: number }>()
  for (const p of partidas) {
    for (const nombre of [p.companero, ...p.rivales]) {
      const limpio = nombre.trim()
      if (!limpio) continue
      const clave = claveDe(limpio)
      const actual = veces.get(clave)
      veces.set(clave, { nombre: actual?.nombre ?? limpio, n: (actual?.n ?? 0) + 1 })
    }
  }
  return [...veces.values()].sort((a, b) => b.n - a.n).map((v) => v.nombre)
}

/* ── El guardado ──────────────────────────────────────────────────────── */

const listaSchema = z.array(partidaSchema)

function claveGuardado(jugadorId: string): string {
  return `domiroyal.libreta.${jugadorId}`
}

/*
 * Lo leído, por cuenta. `useSyncExternalStore` pide la misma referencia
 * mientras no cambie nada, y releer y validar el JSON en cada render la
 * cambiaría siempre.
 */
const leidas = new Map<string, PartidaPersonal[]>()
const oyentes = new Set<() => void>()

function avisar() {
  for (const o of oyentes) o()
}

/** Lo guardado, o nada si no se puede leer: un modo privado, datos rotos. */
export function leerPartidas(jugadorId: string): PartidaPersonal[] {
  const ya = leidas.get(jugadorId)
  if (ya) return ya
  let partidas: PartidaPersonal[] = []
  try {
    const crudo = localStorage.getItem(claveGuardado(jugadorId))
    if (crudo) {
      const leido = listaSchema.safeParse(JSON.parse(crudo))
      if (leido.success) partidas = leido.data
    }
  } catch {
    /* Sin acceso al almacenamiento la libreta arranca vacía. */
  }
  leidas.set(jugadorId, partidas)
  return partidas
}

function escribirPartidas(jugadorId: string, partidas: PartidaPersonal[]): boolean {
  leidas.set(jugadorId, partidas)
  avisar()
  try {
    localStorage.setItem(claveGuardado(jugadorId), JSON.stringify(partidas))
    return true
  } catch {
    return false
  }
}

/* Otra pestaña con la libreta abierta: lo que anota allá se ve acá. */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key?.startsWith('domiroyal.libreta.')) {
      leidas.clear()
      avisar()
    }
  })
}

function suscribir(oyente: () => void) {
  oyentes.add(oyente)
  return () => {
    oyentes.delete(oyente)
  }
}

function nuevoId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

export function useLibretaPersonal(jugadorId: string) {
  const partidas = useSyncExternalStore(
    suscribir,
    () => leerPartidas(jugadorId),
    () => leerPartidas(jugadorId),
  )

  /** Devuelve false si no se pudo guardar en el teléfono. */
  const guardar = useCallback(
    (partida: PartidaPersonal): boolean => {
      const actuales = leerPartidas(jugadorId)
      const otras = actuales.filter((p) => p.id !== partida.id)
      return escribirPartidas(jugadorId, [partida, ...otras])
    },
    [jugadorId],
  )

  const borrar = useCallback(
    (id: string): boolean =>
      escribirPartidas(
        jugadorId,
        leerPartidas(jugadorId).filter((p) => p.id !== id),
      ),
    [jugadorId],
  )

  const crear = useCallback(
    (datos: Pick<PartidaPersonal, 'companero' | 'rivales' | 'objetivo'>): PartidaPersonal => {
      const partida: PartidaPersonal = {
        id: nuevoId(),
        creada: new Date().toISOString(),
        terminada: null,
        manos: [],
        salida: null,
        ...datos,
      }
      guardar(partida)
      return partida
    },
    [guardar],
  )

  return { partidas, guardar, borrar, crear }
}
